import { useCallback, useRef } from 'react';
// modules
import Format from 'modules/Format';
import SearchService from 'modules/Search';
import { useStore } from 'modules/Stores';
import UnderlyingCharts from 'modules/UnderlyingCharts';
import ValidationService from 'modules/Validation';
// typings
import {
  EntityId,
  EntityInfo,
  IChartingLibraryWidget,
  IChartWidgetApi,
} from 'modules/TradingChart/components/TradingView/sources/charting_library.min';
import {
  FORCE_OVERLAY,
  LOCKED,
  STUDY_OPTIONS,
  STUDY_OVERRIDES,
} from 'modules/UnderlyingCharts/constants';

const useUnderlyingCharts = () => {
  const { underlyingChartsList } = useStore('UnderlyingChartsStore');
  const underlyingChartsListRef = useRef(underlyingChartsList);
  underlyingChartsListRef.current = underlyingChartsList;

  const getExchangeTokenOfChart = useCallback((chart: IChartWidgetApi) => {
    const currentSymbol = chart?.symbolExt()?.full_name ?? '';
    return Format.decodeCustomTvExchangeToken(currentSymbol);
  }, []);

  const getUnderlyingExchangeToken = useCallback(
    async (exchangeToken: string) => {
      const data = await SearchService.searchByExchangeToken(exchangeToken);
      const { instrument, segment, underlyingExchangeToken } = data || {};
      if (
        segment &&
        (!ValidationService.isFUTorOPT(instrument) ||
          ValidationService.isCurrency(segment))
      ) {
        return;
      }
      const isCommodity = ValidationService.isCommodity(segment);
      if (underlyingExchangeToken && !isCommodity) {
        return underlyingExchangeToken;
      }
      if (isCommodity) {
        const spotToken = underlyingExchangeToken || exchangeToken;
        const futureScrip = await SearchService.getFutureScripForExpiry(
          spotToken,
        );
        return futureScrip?.exchangeToken;
      }
    },
    [],
  );

  const createUnderlyingChart = useCallback(
    async (
      {
        exchangeToken,
        chart,
      }: {
        exchangeToken: string;
        chart: IChartWidgetApi;
      },
      index: number,
    ) => {
      const underlyingExchangeToken = await getUnderlyingExchangeToken(
        exchangeToken,
      );
      const existingUnderlyingChart =
        UnderlyingCharts.getExistingUnderlyingChartsAtIndex(index);

      if (!underlyingExchangeToken && existingUnderlyingChart) {
        chart.removeEntity(existingUnderlyingChart.id as EntityId);
        UnderlyingCharts.removeUnderlyingChart(
          existingUnderlyingChart.exchangeToken,
          index,
        );
        return;
      }

      if (!underlyingExchangeToken) {
        return;
      }

      if (
        existingUnderlyingChart &&
        existingUnderlyingChart.exchangeToken === exchangeToken
      ) {
        return;
      }

      if (existingUnderlyingChart) {
        chart.removeEntity(existingUnderlyingChart.id as EntityId);
        UnderlyingCharts.removeUnderlyingChart(
          existingUnderlyingChart.exchangeToken,
          index,
        );
      }

      const study = await chart.createStudy(
        'Overlay',
        FORCE_OVERLAY,
        LOCKED,
        {
          symbol: underlyingExchangeToken,
        },
        STUDY_OVERRIDES,
        STUDY_OPTIONS,
      );

      if (study) {
        UnderlyingCharts.addUnderlyingChart({
          chartIndex: index,
          exchangeToken,
          id: study,
        });
      }
    },
    [getUnderlyingExchangeToken],
  );

  const removeAllExistingUnderlyingCharts = useCallback(
    (tvWidget: IChartingLibraryWidget) => {
      const chartsCount = tvWidget.chartsCount();
      underlyingChartsListRef.current.forEach(underlyingChart => {
        const { chartIndex, exchangeToken, id } = underlyingChart;
        const chart =
          chartIndex < chartsCount ? tvWidget.chart(chartIndex) : null;
        if (chart) {
          chart.removeEntity(id as EntityId);
          UnderlyingCharts.removeUnderlyingChart(exchangeToken, chartIndex);
        }
      });
    },
    [],
  );

  const getExistingUnderlyingChart = useCallback(
    async (chart: IChartWidgetApi, study: EntityInfo, index: number) => {
      const existingUnderlyingChartFromList =
        underlyingChartsListRef.current.find(
          ucListItem => ucListItem.id === study.id,
        );

      if (existingUnderlyingChartFromList) {
        // If the underlying chart is already created and exists in the list, return it.
        return existingUnderlyingChartFromList;
      }

      const studyEntity = chart.getStudyById(study.id);
      const exchangeToken = getExchangeTokenOfChart(chart);
      const underlyingExchangeToken = await getUnderlyingExchangeToken(
        exchangeToken,
      );
      // If the underlying chart is already created and does not exist in the list.
      // This can happen when the newly created chart is fetched from the recent layouts
      // and already has the underlying chart. Verifying this by checking if the 'symbol'
      // input's value of this study is equal to the `underlyingExchangeToken`.
      const isUnderlyingChartAlreadyCreated = !!studyEntity
        ?.getInputValues?.()
        .find(
          inputValue =>
            inputValue.id === 'symbol' &&
            inputValue.value === underlyingExchangeToken,
        );

      if (isUnderlyingChartAlreadyCreated) {
        return {
          chartIndex: index,
          exchangeToken,
          id: study.id,
        };
      }
    },
    [getExchangeTokenOfChart, getUnderlyingExchangeToken],
  );

  const handleUnderlyingChartsOnLayoutChange = useCallback(
    (tvWidget: IChartingLibraryWidget) => {
      const chartsCount = tvWidget.chartsCount();
      const underlyingChartsCount = underlyingChartsListRef.current.length;

      if (underlyingChartsCount === chartsCount) return;

      if (underlyingChartsCount > chartsCount) {
        underlyingChartsListRef.current.forEach(underlyingChart => {
          if (underlyingChart.chartIndex >= chartsCount) {
            UnderlyingCharts.removeUnderlyingChart(
              underlyingChart.exchangeToken,
              underlyingChart.chartIndex,
            );
          }
        });
      } else if (underlyingChartsCount < chartsCount) {
        // add underlying charts that are in the new layout
        for (let index = 0; index < chartsCount; index++) {
          const chart = tvWidget.chart(index);
          const studies = chart.getAllStudies();
          let underlyingChartExistsAtIndex = false;
          const studyToBeAdded = studies.find(async study => {
            const existingUnderlyingChart = await getExistingUnderlyingChart(
              chart,
              study,
              index,
            );
            if (existingUnderlyingChart) {
              underlyingChartExistsAtIndex = true;
            }
            // this is for the case where we add a new chart using the layout button
            // and the new chart is created with an underlying chart having the same id
            // as an existing underlying chart from the previous layout.
            return (
              !!existingUnderlyingChart &&
              existingUnderlyingChart.chartIndex !== index
            );
          });
          if (studyToBeAdded) {
            UnderlyingCharts.addUnderlyingChart({
              chartIndex: index,
              exchangeToken: getExchangeTokenOfChart(tvWidget.chart(index)),
              id: studyToBeAdded.id,
            });
          } else if (!underlyingChartExistsAtIndex) {
            // this is for the case where we add a new chart using the layout button
            // and the new chart created does not have an underlying chart already created.
            createUnderlyingChart(
              {
                exchangeToken: getExchangeTokenOfChart(tvWidget.chart(index)),
                chart,
              },
              index,
            );
          }
        }
      }
    },
    [
      createUnderlyingChart,
      getExchangeTokenOfChart,
      getExistingUnderlyingChart,
    ],
  );

  const createUnderlyingCharts = useCallback(
    (tvWidget: IChartingLibraryWidget) => {
      if (!tvWidget.chartsCount()) return;

      const charts = Array.from(
        { length: tvWidget.chartsCount() },
        (_, index) => ({
          exchangeToken: getExchangeTokenOfChart(tvWidget.chart(index)),
          chart: tvWidget.chart(index),
        }),
      );
      charts.forEach((chart, index) => {
        if (chart.exchangeToken) {
          createUnderlyingChart(chart, index);
        }
      });
    },
    [createUnderlyingChart, getExchangeTokenOfChart],
  );

  return {
    createUnderlyingCharts,
    handleUnderlyingChartsOnLayoutChange,
    removeAllExistingUnderlyingCharts,
  };
};

export default useUnderlyingCharts;
